import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook para manejar la lógica del carrusel
 * @param {Array} items - Array de items para el carrusel
 * @param {Object} options - Configuración del carrusel
 * @returns {Object} - { currentIndex, next, prev, goTo, isPaused, pause, resume }
 */
const useCarousel = (items = [], options = {}) => {
  const {
    autoplay = true,
    interval = 8000,
    pauseOnHover = true,
    loop = true
  } = options;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const autoplayRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  // Navegación
  const next = useCallback(() => {
    if (!items.length) return;
    setCurrentIndex((prev) => {
      if (prev === items.length - 1) {
        return loop ? 0 : prev;
      }
      return prev + 1;
    });
  }, [items.length, loop]);

  const prev = useCallback(() => {
    if (!items.length) return;
    setCurrentIndex((prev) => {
      if (prev === 0) {
        return loop ? items.length - 1 : prev;
      }
      return prev - 1;
    });
  }, [items.length, loop]);

  const goTo = useCallback((index) => {
    if (index >= 0 && index < items.length) {
      setCurrentIndex(index);
    }
  }, [items.length]);

  // Control de pausa
  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  // Autoplay
  useEffect(() => {
    if (!autoplay || isPaused || items.length <= 1) {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
      return;
    }

    autoplayRef.current = setInterval(() => {
      next();
    }, interval);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [autoplay, isPaused, items.length, interval, next]);

  // Gestos táctiles (swipe)
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = e.touches[0].clientX;
    pause();
  }, [pause]);

  const handleTouchMove = useCallback((e) => {
    touchEndRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current || !touchEndRef.current) {
      resume();
      return;
    }

    const distance = touchStartRef.current - touchEndRef.current;
    const threshold = 50; // mínimo de px para detectar swipe

    if (Math.abs(distance) > threshold) {
      if (distance > 0) {
        next(); // Swipe izquierda
      } else {
        prev(); // Swipe derecha
      }
    }

    touchStartRef.current = null;
    touchEndRef.current = null;
    resume();
  }, [next, prev, resume]);

  // Handlers para pausar en hover
  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) {
      pause();
    }
  }, [pauseOnHover, pause]);

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover) {
      resume();
    }
  }, [pauseOnHover, resume]);

  return {
    currentIndex,
    currentItem: items[currentIndex] || null,
    next,
    prev,
    goTo,
    isPaused,
    pause,
    resume,
    // Handlers para gestos
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    },
    // Handlers para hover
    hoverHandlers: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    }
  };
};

export default useCarousel;
